import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Reservation } from '@app/shared/interfaces';
import { ReservationService } from '@app/shared/services/reservation/reservation.service';
import { endOfQuarter, startOfQuarter } from 'date-fns';
import { of } from 'rxjs';
import { ReservationAgreementService } from '../../services/reservation-agreements.service';
import { catchError } from 'rxjs/operators';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';

export interface PreviewAgreementDialogData {
  content: string;
}

@Component({
  standalone: false,
  selector: 'sbnb-dialog-preview-agreement',
  templateUrl: './dialog-preview-agreement.component.html',
  styleUrls: ['./dialog-preview-agreement.component.scss'],
})
export class DialogPreviewAgreementComponent implements OnInit {
  loading: boolean;
  loadingPreview: boolean;
  reservations: Reservation[];
  content: string;
  reservationUUID: string | null;
  reservationCodeSearch = '';
  filteredReservations: Reservation[] = [];
  activeReservation: Reservation;
  previewError: string;
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;

  constructor(
    private reservationService: ReservationService,
    private service: ReservationAgreementService,
    @Inject(MAT_DIALOG_DATA) private previewAgreementDialogData: PreviewAgreementDialogData
  ) { }

  ngOnInit() {
    this.loadReservations();
    this.loadPreview();
  }

  loadReservations() {
    const currentDate = new Date();

    // Get the start date and end date of the current quarter
    const startDateOfQuarter = startOfQuarter(currentDate);
    const endDateOfQuarter = endOfQuarter(currentDate);

    this.loading = true;
    this.reservationService.getReservationsByDate(startDateOfQuarter, endDateOfQuarter).subscribe((res: any) => {
      this.loading = false;
      this.reservations = res;
      this.filteredReservations = this.reservations;
      this.updateActiveReservation();
    });
  }

  loadPreview() {
    this.loadingPreview = true;
    this.service
      .previewAgreement(this.previewAgreementDialogData.content, this.reservationUUID)
      .pipe(
        catchError((error) => {
          this.loadingPreview = false;
          if (error.status === 404) {
            this.previewError = 'We were not able to find a reservation against which to preview this agreement.';
          } else {
            this.previewError = error?.error?.message ?? 'There was an error previewing the agreement.';
          }
          return of(null);
        })
      )
      .subscribe((res: any) => {
        this.loadingPreview = false;
        if (res?.content) {
          this.content = res.content;
          this.reservationUUID = res.reservation_uuid;
          this.updateActiveReservation();
        }
      });
  }

  reservationChanged(event: any) {
    if (event.value) {
      this.reservationUUID = event.value;
      this.loadPreview();
    }
  }

  updateActiveReservation() {
    if (this.reservationUUID && this.reservations && this.reservations.length > 0) {
      this.activeReservation = this.reservations.find((reservation) => reservation.uuid === this.reservationUUID);
    }
  }

  onDropdownOpened(isOpened: boolean) {
    if (isOpened) {
      this.reservationCodeSearch = '';
      this.filteredReservations = this.reservations;

      // Focus on the search input
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 100);
    }
  }

  filterReservations() {
    const filterValue = this.reservationCodeSearch.toLowerCase();

    if (this.reservations && this.reservations.length > 0) {
      this.filteredReservations = this.reservations.filter((reservation) => {
        const codeMatches = reservation.code.toLowerCase().includes(filterValue);
        const guestMatches = reservation.guest.toLowerCase().includes(filterValue);

        return codeMatches || guestMatches;
      });
    }
  }
}
